import Instance from '@/models/interfaces/Instance';
import TabletStatus from '@/models/interfaces/TabletStatus';
import ParamsData from '@/models/ParamsData';
import axios, { AxiosInstance, AxiosResponse, CancelTokenSource } from 'axios';
import EnrichmentData from '../models/interfaces/EnrichmentData';
import RatingPartialResponse from '../models/interfaces/RatingPartialResponse';
import RatingSubmitResponse from '../models/interfaces/RatingSubmitResponse';
import SurveySettings from '../models/interfaces/SurveySettings';
import SurveyAnswer from '../models/SurveyAnswer';
import { ExtrasParams } from './ExtrasParams';
import { URLParams } from './URLParams';
import UrlParsedInfo from './UrlParsedInfo';
import FingerprintHandler from './FingerprintHandler';

const INJECTED_SURVEY_SETTINGS_KEY = 'solucx_survey_settings';

export interface SolucxServiceArgs {
  baseURL: string;
  apiKey: string;
  clientId?: string;
  client?: AxiosInstance;
}

/**
 * Classe de serviços para interagir com a API SoluCX
 */
export default class SoluCXService {
  /**
   * Instância do objeto axios para requisições http
   */

  public getTabletStatusToken: CancelTokenSource;
  protected axios: AxiosInstance;
  protected apiKey: string;
  protected clientId: string;
  protected usedInjectedSurveySettings: boolean = false;

  /**
   * Construtor
   * @param baseURL URL da API soluCX
   * @param apiKey chave de token da api para realizar as requisições http
   */
  public constructor({ baseURL, apiKey, clientId = null, client = null }: SolucxServiceArgs) {
    this.apiKey = apiKey;
    this.clientId = clientId;
    this.createAxiosInstance(baseURL, apiKey, client);
  }

  public createAxiosInstance(baseURL: string, apiKey: string, client: AxiosInstance = null) {
    this.axios =
      client ??
      axios.create({
        baseURL,
        headers: {
          common: { 'x-solucx-api-key': apiKey },
        },
      });
  }

  // Canceling requests methods if necessary
  public cancelTabletStatusRequest(): void {
    this.getTabletStatusToken?.cancel();
  }

  // Get ------------------------------------------------------------------------------------

  /**
   * cria uma nova transação e pesquisa
   */
  public async getSurveySettings(urlInfo: UrlParsedInfo): Promise<SurveySettings> {
    const extras: UrlParsedInfo = { ...urlInfo };

    delete extras.transaction_id;
    delete extras.client_id;
    delete extras.employee_id;
    delete extras.store_id;
    delete extras.name;
    delete extras.email;
    delete extras.phone;
    delete extras.phone2;
    delete extras.cpf;
    delete extras.gender;
    delete extras.birth_date;
    delete extras.amount;
    delete extras.rating;

    const params: URLParams = {
      transaction_id: urlInfo.transaction_id,
      client_id: urlInfo.client_id,
      employee_id: urlInfo.employee_id,
      store_id: urlInfo.store_id,
      name: urlInfo.name,
      email: urlInfo.email,
      phone: urlInfo.phone,
      phone2: urlInfo.phone2,
      cpf: urlInfo.cpf,
      gender: urlInfo.gender,
      birth_date: urlInfo.birth_date,
      amount: urlInfo.amount ?? '0',
      journey: urlInfo.journey,
      source: urlInfo.source,
      aval_ref: urlInfo.aval_ref,
      transactionId: +urlInfo.transactionId,
      attemptId: +urlInfo.attemptId,
      rating: undefined, // DISCLAIMER - score aqui é deixado propostitalmente como undefined para não ser enviado na requisição GEt somente na PUT para evitar notas sendo populadas por robôs de verificações de segurança
      overrideJourney: urlInfo.overrideJourney,
    };

    this.convertExtrasParams(params, extras);
    try {
      const injected: SurveySettings = this.takeInjectedSurveySettings(params);
      this.usedInjectedSurveySettings = !!injected;
      const formConfiguration: SurveySettings =
        injected ?? (await this.axios.get<SurveySettings>('rating/form/', { params })).data;

      if (formConfiguration.instance?.stage === 'preview') {
        this.axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL_PREVIEW;
      }

      if (injected) {
        this.postSurveyStarted(params.transactionId, params.attemptId);
      }

      formConfiguration.attemptId = formConfiguration.attemptId?.toString();
      formConfiguration.transactionId = formConfiguration.transactionId?.toString();
      return formConfiguration;
    } catch (error) {
      // @ts-ignore
      console.error('Error to get Survey Settings', error);
      throw error.response.data;
    }
  }

  private takeInjectedSurveySettings(params: URLParams): SurveySettings {
    const settings: SurveySettings = window[INJECTED_SURVEY_SETTINGS_KEY];

    const sameIds =
      Number(params.transactionId) > 0 &&
      Number(settings?.transactionId) === Number(params.transactionId) &&
      Number(settings?.attemptId) === Number(params.attemptId);

    return sameIds ? settings : null;
  }

  private postSurveyStarted(transactionId: number, attemptId: number): void {
    this.axios
      .post('rating/started', { transactionId, attemptId })
      .catch((error) => console.error('Error to mark survey as started', error));
  }

  /**
   * Adquire dados de exibição da jornada
   */
  public async getJourneyParameters(journey: string = null): Promise<Instance> {
    try {
      const response: AxiosResponse<{
        configApp: Instance;
      }> = await this.axios.get<{ configApp: Instance }>('rating/config/');
      return response.data.configApp;
    } catch (error) {
      if (error.response.data && error.response.data.errors) throw error.response.data;
      else throw error;
    }
  }

  // Post ------------------------------------------------------------------------------------

  /**
   * Submete uma avaliação parcial
   * @param rating Avaliação Parcial
   */
  public async postPartialSurvey(
    rating: SurveyAnswer,
    extraParams: ExtrasParams = {},
    trigger: string,
  ): Promise<RatingPartialResponse> {
    const data: ParamsData = {
      partial: true,
      transactionId: rating.transactionId,
      score: rating.score,
      comment: rating.comment,
      anonymous: rating.anonymous,
      attemptId: rating.attemptId,
      items: rating.items,
      isBlockedByCookieQuarantine: rating.isBlockedByCookieQuarantine,
      acceptanceChannels: rating.acceptanceChannels,
      ...extraParams,
    };

    const logQueryString = {
      clientId: this.clientId,
      transactionId: rating?.transactionId,
      attemptId: rating?.attemptId,
      score: rating?.score,
      trigger: trigger,
    };

    const response: AxiosResponse<RatingPartialResponse> = await this.axios.put<RatingPartialResponse>(
      `rating/form/?${this.form(logQueryString)}`,
      data,
    );
    return response.data;
  }

  /**
   * Posta uma avaliação
   * @param rating Avaliação
   */
  public async postSurveyRating(rating: SurveyAnswer): Promise<RatingSubmitResponse> {
    // create response object

    const data: ParamsData = {
      transactionId: rating.transactionId,
      score: rating.score,
      comment: rating.comment,
      anonymous: rating.anonymous,
      acceptanceContactIfDetractor: rating.acceptanceContactIfDetractor,
      acceptanceContactIfNeutral: rating.acceptanceContactIfNeutral,
      acceptanceContactIfPromoter: rating.acceptanceContactIfPromoter,
      attemptId: rating.attemptId,
      items: rating.items,
      src: 3, // TODO: O que fazer com isso
      checkAval: false, // TODO: O que fazer com isso
      source: rating.source,
      isBlockedByCookieQuarantine: rating.isBlockedByCookieQuarantine,
      acceptanceChannels: rating.acceptanceChannels,
    };

    const logQueryString = {
      clientId: this.clientId,
      transactionId: rating?.transactionId,
      attemptId: rating?.attemptId,
      score: rating?.score,
    };

    const fingerprintHandler: FingerprintHandler = new FingerprintHandler(rating?.journeyParameters);

    const fingerprint: string = await fingerprintHandler.getFingerprint();
    const objFingerprint: Record<string, string> = fingerprint ? { 'fingerprint-visitor-id': fingerprint } : {};

    const headersCommon = this.axios.defaults.headers.common;
    this.axios.defaults.headers.common = {
      ...headersCommon,
      ...objFingerprint,
    };

    const response: AxiosResponse<RatingSubmitResponse> = await this.axios.post<RatingSubmitResponse>(
      `rating/form?${this.form(logQueryString)}`,
      data,
    );

    localStorage.removeItem('solucx@fingerprintVisitorId');
    return response.data;
  }

  /**
   * Submete dados de enriquecimento de base
   * @param enrichmentData Dados de enriquecimento
   */
  public async postEnrichment(enrichmentData: EnrichmentData): Promise<void> {
    const logQueryString = {
      clientId: this.clientId,
      transactionId: enrichmentData?.transactionId,
      attemptId: enrichmentData?.attemptId,
    };

    await this.axios.put<EnrichmentData>(`rating/form?${this.form(logQueryString)}`, enrichmentData);
  }

  // Tablet ------------------------------------------------------------------------------------

  /**
   * Gets tablet status
   * @param employeeId
   * @param storeId
   * @param solucxId
   * @returns tablet status
   */
  public async getTabletStatus(employeeId: string, storeId: string, solucxId: boolean): Promise<TabletStatus> {
    const params: ParamsData = {
      employee: employeeId,
      store: storeId,
      solucx_id: solucxId,
    };

    try {
      this.getTabletStatusToken = axios.CancelToken.source();
      const response: AxiosResponse<TabletStatus> = await this.axios.get<TabletStatus>('tablet/status/', {
        params,
        cancelToken: this.getTabletStatusToken.token,
      });
      return response.data;
    } catch (error) {
      if (error.response.data && error.response.data.errors) throw error.response.data;
      else throw error;
    }
  }

  /**
   * Posta uma avaliação
   * @param survey Avaliação
   */
  public async postTabletStatus(
    employeeId: string,
    storeId: string,
    showRating: boolean,
    solucxId: boolean,
  ): Promise<RatingSubmitResponse> {
    // create response object

    const data: ParamsData = {
      employee: employeeId,
      store: storeId,
      show_rating: showRating,
      solucx_id: solucxId,
    };

    const response: AxiosResponse<RatingSubmitResponse> = await this.axios.post<RatingSubmitResponse>(
      '/tablet/status/',
      data,
    );
    return response.data;
  }

  // Unsubscribe ------------------------------------------------------------------------------------

  public async unsubscribe(contact: string, transactionId?: string, channel?: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.axios.post<boolean>('rating/unsubscribe/', {
      contact,
      transactionId,
      channel,
    });
    return response.data;
  }

  public async checkBudget(transactionId: string): Promise<boolean> {
    // Com a pesquisa injetada, o widget-api já validou a cota da loja no preflight e só
    // devolveu o payload porque ela passou. Repetir aqui é um round-trip bloqueando a abertura.
    if (this.usedInjectedSurveySettings) return true;

    try {
      const response: AxiosResponse<boolean> = await this.axios.get<boolean>(
        `rating/widget/?transactionId=${transactionId}`,
      );
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  }

  // Utils ------------------------------------------------------------------------------------

  /**
   * Transforma um dicionário em uma string do tipo http-form
   * TODO: fazer backed interpretar não só "form" data
   */
  private form(data: { [key: string]: any }): string {
    let form: string = '';
    for (const i in data) form += (i + '=' + data[i] ?? '') + '&';
    return form;
  }

  private convertExtrasParams(params: URLParams, data: UrlParsedInfo): void {
    for (const key in data) {
      if (key.indexOf('param_') === -1) continue;
      params[key] = data[key];
    }
  }
}
